// src/components/layout/Sidebar.tsx
import { useSelector } from 'react-redux';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Home, ListChecks, FilePlus2, Inbox, Star, CalendarDays, ClipboardList, CheckCircle2,
  LayoutGrid, CalendarClock, Footprints, BarChart3, ShieldCheck, type LucideIcon,
} from 'lucide-react';
import { RootState } from 'store';
import { taskService } from 'services/taskService';
import { meetingService } from 'services/meetingService';
import { SIDEBAR_LISTS } from 'utils/constants';
import { isOverdue, TODAY, addDays } from 'utils/dateUtils';
import { Task } from 'types/task';
import { Meeting } from 'types/meeting';
import { usePermission } from 'hooks/usePermission';

// Line-icon set (Lucide) for nav — SIDEBAR_LISTS still carries an emoji
// `icon` field too, since that's used verbatim inside <option> elements
// elsewhere (Project select dropdowns), which can't render SVG/components.
const SMART_LIST_ICONS: Record<string, LucideIcon> = {
  assign: FilePlus2, mine: Inbox, important: Star, week: CalendarDays, all: ClipboardList, done: CheckCircle2,
};

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { listId: curList } = useParams<{ listId: string }>();
  const { user } = useSelector((s: RootState) => s.auth);
  const canManageMeetings = usePermission('canManageMeetings');
  const canManageVisitors = usePermission('canManageVisitors');

  const { data } = useQuery({
    queryKey: ['tasks'],
    queryFn: () => taskService.getAll({ limit: 500 }),
  });

  const { data: upcomingMeetingsData } = useQuery({
    queryKey: ['meetings', 'upcoming'],
    queryFn: () => meetingService.getAll('upcoming'),
  });
  const upcomingMeetings: Meeting[] = upcomingMeetingsData || [];

  const tasks: Task[] = data?.tasks || [];
  // Every list except Completed only ever shows still-open tasks — a
  // completed task moves out of All Tasks/Work/etc. entirely (see TaskList.tsx).
  const active = tasks.filter(t => t.status !== 'Completed');

  // Compute counts
  const counts: Record<string, number> = {
    assign:    active.filter(t => t.myDay).length,
    important: active.filter(t => t.starred || t.priority === 'Emergency' || t.priority === 'High').length,
    planned:   active.filter(t => t.due).length,
    mine:      active.filter(t => t.assignedTo?._id === user?._id).length,
    overdue:   active.filter(t => isOverdue(t.due)).length,
    approval:  active.filter(t => t.status === 'Review' && t.assignedBy?._id === user?._id).length,
    week:      active.filter(t => !!t.due && t.due! >= TODAY && t.due! <= addDays(7)).length,
    all:       active.filter(t => t.assignedBy?._id === user?._id).length,
    done:      tasks.filter(t => t.status === 'Completed').length,
  };

  function SbBtn({ id, label, icon: Icon, danger }: { id: string; label: string; icon: LucideIcon; danger?: boolean }) {
    const active = curList === id;
    return (
      <button
        onClick={() => navigate(`/tasks/${id}`)}
        className={`w-full text-left px-3 py-2 rounded-lg border-l-2 mb-0.5 flex items-center gap-2.5 text-sm transition-all whitespace-nowrap ${
          active
            ? 'border-l-blue-400 bg-blue-500/15 text-[var(--om-text)] font-semibold'
            : `border-l-transparent ${danger ? 'text-red-300 hover:bg-red-500/10 hover:text-red-200' : 'text-[var(--om-text-secondary)] hover:text-[var(--om-text)] hover:bg-[var(--om-overlay-10)]'}`
        }`}
      >
        <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={2} />
        <span className="flex-1 tracking-wide">{label}</span>
        {counts[id] > 0 && (
          <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-md ${active ? 'bg-blue-500/30 text-blue-200' : 'bg-[var(--om-overlay-10)] text-[var(--om-text-secondary)]'}`}>
            {counts[id]}
          </span>
        )}
      </button>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {/* Dashboard */}
      <div className="px-3 pt-4 pb-2">
        <button
          onClick={() => navigate('/dashboard')}
          className={`w-full text-left px-3 py-2 rounded-lg border-l-2 mb-0.5 flex items-center gap-2.5 text-sm transition-all whitespace-nowrap ${
            location.pathname === '/dashboard'
              ? 'border-l-blue-400 bg-blue-500/15 text-[var(--om-text)] font-semibold'
              : 'border-l-transparent text-[var(--om-text-secondary)] hover:text-[var(--om-text)] hover:bg-[var(--om-overlay-10)]'
          }`}
        >
          <Home className="w-4 h-4 flex-shrink-0" strokeWidth={2} />
          <span className="flex-1 tracking-wide">Dashboard</span>
        </button>
      </div>

      <div className="h-px bg-[var(--om-overlay-10)] mx-3 my-1" />

      {/* Smart Lists */}
      <div className="px-3 py-2">
        <span className="text-[11px] text-[var(--om-text-dim)] font-bold uppercase tracking-[0.15em] px-1 flex items-center gap-1.5 mb-2">
          <ListChecks className="w-3.5 h-3.5" strokeWidth={2} /> Smart Lists
        </span>
        {SIDEBAR_LISTS.map(l => (
          <SbBtn key={l.id} id={l.id} label={l.label} icon={SMART_LIST_ICONS[l.id] || ClipboardList} danger={'danger' in l && !!l.danger} />
        ))}
      </div>

      <div className="h-px bg-[var(--om-overlay-10)] mx-3 my-1" />

      {/* Operations */}
      <div className="px-3 py-2">
        <span className="text-[11px] text-[var(--om-text-dim)] font-bold uppercase tracking-[0.15em] px-1 flex items-center gap-1.5 mb-2">
          <LayoutGrid className="w-3.5 h-3.5" strokeWidth={2} /> Operations
        </span>
        {canManageMeetings && (
        <button
          onClick={() => navigate('/meetings')}
          className={`w-full text-left px-3 py-2 rounded-lg border-l-2 mb-0.5 flex items-center gap-2.5 text-sm transition-all whitespace-nowrap ${
            location.pathname === '/meetings'
              ? 'border-l-blue-400 bg-blue-500/15 text-[var(--om-text)] font-semibold'
              : 'border-l-transparent text-[var(--om-text-secondary)] hover:text-[var(--om-text)] hover:bg-[var(--om-overlay-10)]'
          }`}
        >
          <CalendarClock className="w-4 h-4 flex-shrink-0" strokeWidth={2} />
          <span className="flex-1 tracking-wide">Meetings</span>
          {upcomingMeetings.length > 0 && (
            <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-md ${location.pathname === '/meetings' ? 'bg-blue-500/30 text-blue-200' : 'bg-[var(--om-overlay-10)] text-[var(--om-text-secondary)]'}`}>
              {upcomingMeetings.length}
            </span>
          )}
        </button>
        )}
        {canManageVisitors && (
        <button
          onClick={() => navigate('/visitors')}
          className={`w-full text-left px-3 py-2 rounded-lg border-l-2 mb-0.5 flex items-center gap-2.5 text-sm transition-all whitespace-nowrap ${
            location.pathname === '/visitors'
              ? 'border-l-blue-400 bg-blue-500/15 text-[var(--om-text)] font-semibold'
              : 'border-l-transparent text-[var(--om-text-secondary)] hover:text-[var(--om-text)] hover:bg-[var(--om-overlay-10)]'
          }`}
        >
          <Footprints className="w-4 h-4 flex-shrink-0" strokeWidth={2} />
          <span className="flex-1 tracking-wide">Visitors</span>
        </button>
        )}
        <button
          onClick={() => navigate('/report')}
          className={`w-full text-left px-3 py-2 rounded-lg border-l-2 mb-0.5 flex items-center gap-2.5 text-sm transition-all whitespace-nowrap ${
            location.pathname === '/report'
              ? 'border-l-blue-400 bg-blue-500/15 text-[var(--om-text)] font-semibold'
              : 'border-l-transparent text-[var(--om-text-secondary)] hover:text-[var(--om-text)] hover:bg-[var(--om-overlay-10)]'
          }`}
        >
          <BarChart3 className="w-4 h-4 flex-shrink-0" strokeWidth={2} />
          <span className="flex-1 tracking-wide">Reports</span>
        </button>
      </div>

      <div className="h-px bg-[var(--om-overlay-10)] mx-3 my-1" />

      {/* Admin — only shown to admin accounts; the route itself is also gated */}
      {user?.isAdmin && (
      <div className="px-3 py-2">
        <button
          onClick={() => navigate('/admin')}
          className="w-full text-left px-3 py-2 rounded-lg border-l-2 border-l-transparent mb-0.5 flex items-center gap-2.5 text-sm transition-all whitespace-nowrap text-[var(--om-text-secondary)] hover:text-[var(--om-text)] hover:bg-[var(--om-overlay-10)]"
        >
          <ShieldCheck className="w-4 h-4 flex-shrink-0" strokeWidth={2} />
          <span className="flex-1 tracking-wide">Admin Dashboard</span>
        </button>
      </div>
      )}
    </div>
  );
}
